import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, XIcon } from 'lucide-react';
type Resource = {
  icon: string;
  title: string;
  label: string;
  subtitle: string;
  problem: string;
  details: string[];
  result: string;
};
const resources: Resource[] = [{
  icon: '📗',
  label: 'RESOURCE 1 — 120 Ready-to-Teach English Lessons',
  title: '120 Ready-to-Teach English Lessons',
  subtitle: 'The complete lesson library your students need — and the one you have always needed as a teacher.',
  problem: 'Building lessons from scratch is the single biggest drain on every ESL teacher’s time and energy. This library gives you 120 fully structured lessons across 6 progressive volumes — so you never have to build a lesson from zero again.',
  details: ['A1 Foundations through B1 Independent Communication', 'Grammar, vocabulary, speaking, reading, writing, and assessment', 'Objectives, warm-ups, teacher explanations, pair work, games, answer keys, and homework'],
  result: 'Your students progress from their first class to independent communication — and every lesson is ready to deliver.'
}, {
  icon: '📘',
  label: 'RESOURCE 2 — ESL Speaking Activity Bank',
  title: '100 Ready-to-Use Speaking Activities',
  subtitle: 'Get every student talking — consistently, confidently, and without the awkward silence.',
  problem: 'The hardest part of teaching ESL is not grammar. It is getting students to actually speak. This bank gives you activities for A1, A2, and B1 that drop into any lesson at any moment.',
  details: ['Pair work, role-plays, information gaps, surveys, storytelling, and mini debates', 'Conversation cards and real-life scenario tasks', 'Objective, level, duration, steps, variations, and extensions for every activity'],
  result: 'Students speak more, speak with confidence, and leave every class having used the language.'
}, {
  icon: '📙',
  label: 'RESOURCE 3 — English Grammar Made Simple',
  title: 'Teacher-Friendly Grammar Guide',
  subtitle: 'Explanations, examples, and practice activities written for teachers — not textbooks.',
  problem: 'Every ESL teacher has stood mid-explanation unsure how to make a grammar structure clear. This resource gives you 40+ A1 to B1 structures explained the way teachers actually need them.',
  details: ['Present, past, future, conditionals, passive voice, reported speech, and more', 'Simple rules, examples, common mistakes, and guided practice', 'Written exercises, oral activities, mini quizzes, and answer keys'],
  result: 'You walk into every grammar lesson knowing exactly what to say and what mistakes to watch for.'
}, {
  icon: '📕',
  label: 'RESOURCE 4 — ESL Assessment and Quiz Bank',
  title: 'Ready-to-Use Tests, Reviews & Answer Keys',
  subtitle: 'Every test day is prepared before it arrives.',
  problem: 'Building assessments from scratch takes hours. This resource gives you a complete bank of tests, quizzes, and evaluations for every level, skill, and course stage.',
  details: ['Placement tests, diagnostics, A1/A2/B1 tests, reviews, partials, and finals', 'Reading, writing, speaking, grammar, and vocabulary evaluations', 'Rubrics, scoring guides, exit tickets, and complete answer keys'],
  result: 'Your students always know where they stand, and you always have the right assessment ready.'
}, {
  icon: '📓',
  label: 'RESOURCE 5 — 30 Emergency ESL Lessons',
  title: 'No-Prep Classes for Busy Days',
  subtitle: 'Open, choose, teach — even when the plan falls apart.',
  problem: 'A class gets added at the last minute, a substitute is needed, or you have twenty minutes and nothing ready. These 30 complete lessons require zero preparation.',
  details: ['Topics from introductions and food to travel, jobs, opinions, and review lessons', 'Warm-up, vocabulary, oral task, reading or writing, review, and teacher notes', 'Flexible activities across all levels'],
  result: 'You will never walk into a class unprepared again.'
}, {
  icon: '📔',
  label: 'RESOURCE 6 — Mixed-Level Classroom Survival Guide',
  title: 'Mixed-Level Classroom Survival Guide',
  subtitle: 'Simple strategies for teaching students at different English levels.',
  problem: 'Mixed-level classrooms are one of the most common and stressful realities of ESL teaching. This guide gives you a clear practical system for managing it without losing anyone in the room.',
  details: ['Grouping, differentiation, simplified instructions, and extension activities', 'Adaptation tables for reading, oral tasks, and written exercises', 'Flexible rubrics, checklists, and classroom management strategies'],
  result: 'Mixed-level classes stop being stressful and become something you manage with confidence.'
}];
export function MethodologySections() {
  return <>
      <section className="bg-black px-4 py-16 sm:py-24" data-id="element-98">
        <div className="mx-auto max-w-3xl" data-id="element-99">
          <div className="text-center" data-id="element-100">
            <h2 className="font-display text-3xl font-bold text-white sm:text-4xl" data-id="element-101">
              Does this sound familiar?
            </h2>
            <p className="mt-6 rounded-2xl border border-line bg-charcoal p-6 text-left text-base leading-7 text-stone-300" data-id="element-102">
              Sunday night. 10 PM. Tomorrow morning you have an English class to
              teach. And you are still searching online, pulling activities from
              three different websites, trying to build a lesson that flows,
              fits your students’ level, and covers grammar, speaking, and
              writing.
              <br data-id="element-103" />
              <br data-id="element-104" />
              <span className="font-semibold text-gold-100" data-id="element-105">
                Not because you do not know how to teach English.
              </span>{' '}
              Because no one ever gave you a complete, ready-to-teach system.
            </p>
          </div>

          <ul className="mt-8 space-y-3" data-id="element-106">
            {['Nights lost searching for activities that work, flow, and fit your students’ level.', 'Lessons that feel repetitive because you keep falling back on what you already know.', 'Visible improvisation that quietly chips away at your confidence as a teacher.', 'No real progression across weeks — and students who slowly lose motivation.', 'Weekends spent planning instead of resting, recovering, or living your life.'].map(point => <li key={point} className="flex gap-3 rounded-xl border border-red-900/40 bg-charcoal p-4 text-sm text-stone-300" data-id="element-107">
                <span data-id="element-108">🔴</span>
                {point}
              </li>)}
          </ul>
          <p className="mt-10 text-center font-display text-2xl font-bold text-gold-300" data-id="element-109">
            Stop giving away your free time. Plan more than a full year of
            English classes — starting today.
          </p>
        </div>
      </section>

      <section className="border-y border-line bg-charcoal px-4 py-16 sm:py-20" data-id="element-110">
        <div className="mx-auto max-w-4xl text-center" data-id="element-111">
          <span className="text-3xl" data-id="element-112">🏆</span>
          <h2 className="mt-4 font-display text-3xl font-bold text-white sm:text-4xl" data-id="element-113">
            The most complete ESL teaching system for English teachers worldwide
          </h2>
          <p className="mt-3 text-lg font-bold text-gold-300" data-id="element-114">
            ESL Teacher Ready™
          </p>
          <p className="mt-2 text-stone-400" data-id="element-115">
            6 Complete Resources · 120 Ready-to-Teach Lessons · 5 Strategic
            Bonuses · Everything ready to use from today.
          </p>
          <div className="mt-9 grid gap-5 text-left md:grid-cols-2" data-id="element-116">
            <div className="space-y-4 rounded-2xl border border-line bg-black p-6 text-sm text-stone-400" data-id="element-117">
              {['Not random activities pulled from the internet.', 'Not a theory book you need to rewrite before it works.', 'Not content designed for a different type of teacher.'].map(text => <p key={text} className="flex gap-3" data-id="element-118">
                  <XIcon className="shrink-0 text-red-500" size={18} data-id="element-119" />
                  {text}
                </p>)}
            </div>
            <div className="rounded-2xl border border-gold-700 bg-gold-700/10 p-6 text-base leading-7 text-white" data-id="element-120">
              <p className="flex gap-3" data-id="element-121">
                <CheckCircle2Icon className="mt-1 shrink-0 text-gold-300" size={20} data-id="element-122" />
                This is a complete 6-in-1 professional teaching system — so you
                walk into every class fully prepared, confident, and ready to
                deliver a lesson your students will actually remember.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-black px-4 py-16 sm:py-24" data-id="element-123">
        <div className="mx-auto max-w-6xl" data-id="element-124">
          <div className="text-center" data-id="element-125">
            <h2 className="font-display text-3xl font-bold text-white sm:text-5xl" data-id="element-126">
              📚 6 Complete Resources in One Single System
            </h2>
            <p className="mt-3 text-gold-100" data-id="element-127">
              120 Lessons · A1 to B1 · 40+ Weeks of Fresh Content · Nothing to
              Build From Scratch
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2" data-id="element-128">
            {resources.map((resource, index) => <motion.article key={resource.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.07
          }} className="flex flex-col rounded-2xl border border-line bg-charcoal p-7" data-id="element-129">
                <div className="flex items-center gap-4" data-id="element-130">
                  <span className="text-4xl" data-id="element-131">{resource.icon}</span>
                  <div data-id="element-132">
                    <p className="text-xs font-bold uppercase tracking-wide text-gold-300" data-id="element-133">
                      {resource.label}
                    </p>
                    <h3 className="mt-1 font-display text-2xl font-bold text-white" data-id="element-134">
                      {resource.title}
                    </h3>
                  </div>
                </div>
                <p className="mt-6 border-l-2 border-gold-500 pl-4 text-sm italic leading-6 text-stone-200" data-id="element-135">
                  {resource.subtitle}
                </p>
                <div className="mt-6" data-id="element-136">
                  <h4 className="text-xs font-bold uppercase tracking-wide text-stone-500" data-id="element-137">
                    What does it solve in your teaching?
                  </h4>
                  <p className="mt-2 text-sm leading-6 text-stone-400" data-id="element-138">
                    {resource.problem}
                  </p>
                </div>
                <ul className="mt-6 space-y-2 text-sm text-stone-400" data-id="element-139">
                  {resource.details.map(item => <li key={item} className="flex gap-2" data-id="element-140">
                      <CheckCircle2Icon size={17} className="shrink-0 text-gold-300" data-id="element-141" />
                      {item}
                    </li>)}
                </ul>
                <div className="mt-6 rounded-xl border border-gold-700/50 bg-gold-700/10 p-4 text-sm text-gold-100" data-id="element-142">
                  <strong className="text-gold-300" data-id="element-143">Result: </strong>
                  {resource.result}
                </div>
              </motion.article>)}
          </div>
        </div>
      </section>
    </>;
}